import {
  useEffect, useRef, useState
} from "react";
import { MicrophoneDeviceInfo } from "@videosdk.live/react-sdk";
import { BlobOptions } from "node:buffer";
import {
  Button, Space, Popover, Progress, Row, Col, Divider,
  Typography, App, Flex
} from "antd";
import {
  AudioOutlined, CheckOutlined, AudioMutedOutlined, PauseCircleOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import useVideoStore from "../../hooks/useVideoStore";

const { Text } = Typography;

const MicControls = ({
  customAudioStream,
  mics,
  changeMic,
  audioTrack,
  micOn,
  didDeviceChange,
  setDidDeviceChange
}: {
  customAudioStream: MediaStream | null;
  mics: MicrophoneDeviceInfo[];
  changeMic: (deviceId: string) => void;
  audioTrack: any;
  micOn: boolean;
  didDeviceChange: boolean;
  setDidDeviceChange: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { t } = useTranslation();
  const {
    setSelectedMic,
    selectedMic,
    selectedSpeaker,
    isMicrophonePermissionAllowed
  } = useVideoStore();
  const { message } = App.useApp();
  const [audioProgress, setAudioProgress] = useState(0);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [recordingStatus, setRecordingStatus] = useState("inactive");
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [volume, setVolume] = useState<number | null>(null);
  const [audio, setAudio] = useState<string | null>(null);

  const audioTrackRef = useRef();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioAnalyserIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);

  const recorderOptions: Pick<MediaRecorderOptions, "mimeType"> = { mimeType: "audio/webm" };
  const blobOptions: Pick<BlobOptions, "type"> = { type: "audio/webm" };

  const analyseAudio = (audioStreamTrack: MediaStreamTrack) => {
    const audioStream = new MediaStream([audioStreamTrack]);
    const audioContext = new AudioContext();
    const audioSource = audioContext.createMediaStreamSource(audioStream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 512;
    analyser.minDecibels = -127;
    analyser.maxDecibels = 0;
    analyser.smoothingTimeConstant = 0.4;

    audioSource.connect(analyser);

    const volumes = new Uint8Array(analyser.frequencyBinCount);
    const volumeCallback = () => {
      analyser.getByteFrequencyData(volumes);
      const volumeSum = volumes.reduce((sum, vol) => sum + vol);
      const averageVolume = volumeSum / volumes.length;
      setVolume(averageVolume);
    };
    audioAnalyserIntervalRef.current = setInterval(volumeCallback, 100);
  };

  const stopAudioAnalyse = () => {
    clearInterval(audioAnalyserIntervalRef.current!);
  };

  useEffect(() => {
    audioTrackRef.current = audioTrack;

    if (audioTrack) {
      analyseAudio(audioTrack);
    } else {
      stopAudioAnalyse();
    }
  }, [audioTrack]);

  const handlePlaying = () => {
    setRecordingStatus("playing");
    const audioTags = document.getElementsByTagName("audio");

    for (let i = 0; i < audioTags.length; i += 1) {
      if (audioTags.item(i) !== null) {
        // @ts-ignore
        audioTags.item(i).setSinkId(selectedSpeaker.id).then(() => {
          audioTags.item(i)?.play();
          audioTags.item(i)?.addEventListener("timeupdate", () => {
            // @ts-ignore
            const progress = (audioTags.item(i).currentTime / recordingDuration) * 100;
            setAudioProgress(progress);
          });
          audioTags.item(i)?.addEventListener("ended", () => {
            setAudioProgress(0);
          });
        });
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current?.state !== "inactive") {
      setRecordingProgress(0);
      setRecordingStatus("stopped recording");
      clearInterval(intervalRef.current!);
      mediaRecorder.current?.stop();
    }
  };

  const startRecording = async () => {
    setRecordingStatus("recording");

    if (customAudioStream === null) {
      message.error("Error in MediaRecorder");
      return;
    }

    try {
      mediaRecorder.current = new MediaRecorder(customAudioStream, recorderOptions);
      mediaRecorder.current.start();
      let localAudioChunks: Blob[] = [];

      mediaRecorder.current.ondataavailable = (event: BlobEvent) => {
        if (typeof event.data === "undefined") return;
        if (event.data.size === 0) return;
        localAudioChunks.push(event.data);
      };

      const startTime = Date.now();

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(localAudioChunks, blobOptions);
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudio(audioUrl);
        localAudioChunks = [];
        const elapsedTime = Date.now() - startTime;
        const durationInSeconds = (elapsedTime / 1000);
        setRecordingDuration(durationInSeconds);
      };

      intervalRef.current = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const progress = (elapsedTime / 7000) * 100;
        setRecordingProgress(progress);
      });

      setTimeout(() => {
        clearInterval(intervalRef.current!);
        stopRecording();
      }, 7000);
    } catch (err) {
      message.error(`Error in MediaRecorder: ${err}`);
    }
  };

  useEffect(() => {
    if (didDeviceChange) {
      setDidDeviceChange(false);
      if (mediaRecorder.current != null && mediaRecorder.current.state === "recording") { stopRecording(); }
      setRecordingProgress(0);
      setRecordingStatus("inactive");
    }
  }, [didDeviceChange]);

  return (
    <Popover
      content={(
        <Space direction="vertical" size={0}>
          {mics.map(
            (item, index) => {
              return item?.kind === "audioinput" && (
                <Button
                  key={item.deviceId}
                  type="text"
                  onClick={() => {
                    setSelectedMic({ label: item?.label, id: item?.deviceId });
                    changeMic(item?.deviceId);
                    if (mediaRecorder.current != null && mediaRecorder.current.state === "recording") {
                      stopRecording();
                    }
                    setRecordingProgress(0);
                    setRecordingStatus("inactive");
                  }}
                  block
                >
                  <Row gutter={8} style={{ width: "100%" }}>
                    <Col span={2}>
                      {selectedMic?.label === item?.label && (
                        <CheckOutlined/>
                      )}
                    </Col>
                    <Col span={22} style={{ textAlign: "left" }}>
                      <Text ellipsis>
                        {item?.label ? item?.label : `Mic ${index + 1}`}
                      </Text>
                    </Col>
                  </Row>
                </Button>
              );
            }
          )}

          <Divider style={{ margin: "8px 0" }}/>

          {micOn ? (
            <Row align="middle" gutter={16}>
              <Col>
                <AudioOutlined/>
              </Col>
              <Col style={{ flexGrow: 1 }}>
                <Progress percent={(volume! / 256) * 100} showInfo={false}/>
              </Col>
              <Col>
                <Button
                  type="primary"
                  block
                  style={{ width: "100px" }}
                  onClick={() => {
                    if (recordingStatus === "inactive") {
                      startRecording();
                      return;
                    }
                    if (recordingStatus === "stopped recording") {
                      handlePlaying();
                      return;
                    }
                    if (recordingStatus === "recording") {
                      stopRecording();
                      return;
                    }
                    if (recordingStatus === "playing") {
                      handlePlaying();
                    }
                  }}
                >
                  {recordingStatus === "inactive" && "Record"}
                  {recordingStatus === "stopped recording" && "Play"}
                  {recordingStatus === "recording" && (
                    <>
                      <span style={{
                        zIndex: 1,
                        position: "relative",
                      }}
                      >
                        <PauseCircleOutlined />
                      </span>
                      <span
                        style={{
                          width: `${recordingProgress}%`,
                          height: "100%",
                          background: "#6F767E",
                          position: "absolute",
                          top: 0,
                          left: 0
                        }}
                      />
                    </>
                  )}
                  {recordingStatus === "playing" && (
                    <>
                      <span style={{
                        zIndex: 1,
                        position: "relative",
                      }}
                      >
                        <PauseCircleOutlined/>
                      </span>
                      <span
                        style={{
                          width: `${audioProgress}%`,
                          height: "100%",
                          background: "#6F767E",
                          position: "absolute",
                          top: 0,
                          left: 0
                        }}
                      />
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          )
            : (
              <Space style={{ textAlign: "right", width: "100%" }}>
                <AudioMutedOutlined/>
                Unmute to test your mic
              </Space>
            )}
          <audio src={audio!}>
            <track kind="captions"/>
          </audio>
        </Space>
      )}
      trigger="click"
      placement="topLeft"
    >
      <Button
        disabled={!isMicrophonePermissionAllowed}
        onClick={() => {
          if (mediaRecorder.current !== null && mediaRecorder.current.state === "recording") {
            stopRecording();
          }
          setRecordingProgress(0);
          setRecordingStatus("inactive");
        }}
        block
      >
        <Flex gap={10} style={{ width: "100%", color: "black", opacity: ".25" }}>
          <AudioOutlined/>
          <Text ellipsis>
            {isMicrophonePermissionAllowed ? selectedMic?.label : t("video.permissionNeeded")}
          </Text>
        </Flex>
      </Button>
    </Popover>
  );
};

export default MicControls;
